import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getWebsiteConfig, saveWebsiteConfig, trainWebsite, getCrawlStatus } from '../../services/configService';

const IN_PROGRESS = new Set(['crawling', 'ready', 'ingesting']);

const STATUS_MAP = {
  idle:             { label: 'Not trained yet',     color: '#475569', bg: 'rgba(71,85,105,0.1)',    border: 'rgba(71,85,105,0.2)',    spinner: false },
  crawling:         { label: 'Crawling website…',   color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', spinner: true  },
  ready:            { label: 'Indexing content…',   color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', spinner: true  },
  ingesting:        { label: 'Indexing content…',   color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', border: 'rgba(245,158,11,0.25)', spinner: true  },
  ingested:         { label: 'Trained',             color: '#10b981', bg: 'rgba(16,185,129,0.08)', border: 'rgba(16,185,129,0.2)',  spinner: false },
  error:            { label: 'Crawl failed',        color: '#f87171', bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.2)',   spinner: false },
  ingestion_failed: { label: 'Indexing failed',     color: '#f87171', bg: 'rgba(239,68,68,0.08)',  border: 'rgba(239,68,68,0.2)',   spinner: false },
};

function isValidUrl(str) {
  try { return Boolean(new URL(str)); } catch { return false; }
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function cleanUrl(u) {
  if (!u) return '';
  try { const { host, pathname } = new URL(u); return host + (pathname && pathname !== '/' ? pathname : ''); }
  catch { return u.replace(/^https?:\/\//, '').replace(/\/$/, ''); }
}

function StatusChip({ status, crawlCompletedAt, websiteUrl }) {
  const s = STATUS_MAP[status] || STATUS_MAP.idle;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '7px 12px', borderRadius: 8,
        background: s.bg, border: `1px solid ${s.border}`,
        alignSelf: 'flex-start',
      }}>
        {s.spinner ? (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={s.color} strokeWidth="2.5" strokeLinecap="round"
            style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}>
            <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
          </svg>
        ) : status === 'ingested' ? (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={s.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        ) : (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={s.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        )}
        <span style={{ fontSize: 13, fontWeight: 600, color: s.color }}>{s.label}</span>
      </div>
      {status === 'ingested' && (websiteUrl || crawlCompletedAt) && (
        <span style={{ fontSize: 11.5, color: '#475569', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {websiteUrl && (
            <span style={{ color: '#94a3b8', fontWeight: 500, wordBreak: 'break-all' }}>{cleanUrl(websiteUrl)}</span>
          )}
          {websiteUrl && crawlCompletedAt && <span style={{ opacity: 0.5 }}>·</span>}
          {crawlCompletedAt && <span>Last trained {formatDate(crawlCompletedAt)}</span>}
        </span>
      )}
    </div>
  );
}

export default function WebsiteTrain() {
  const { user } = useAuth();

  const [url, setUrl]             = useState('');
  const [saved, setSaved]         = useState('');
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [saveError, setSaveError] = useState('');

  const [crawlStatus, setCrawlStatus]     = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [training, setTraining]           = useState(false);

  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    if (!user) return;
    try {
      const data = await getCrawlStatus();
      setCrawlStatus(data);
    } catch {
      // silently ignore status errors — don't block the UI
    } finally {
      setStatusLoading(false);
    }
  }, [user]);

  // Initial loads
  useEffect(() => {
    if (!user) return;
    setLoading(true);
    getWebsiteConfig()
      .then(({ config }) => {
        const u = config?.websiteUrl || '';
        setUrl(u);
        setSaved(u);
      })
      .catch(() => setSaveError('Failed to load website config.'))
      .finally(() => setLoading(false));

    loadStatus();
  }, [user, loadStatus]);

  // Poll while in-progress
  useEffect(() => {
    const isRunning = IN_PROGRESS.has(crawlStatus?.status);
    if (!isRunning) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return; // already polling
    pollRef.current = setInterval(loadStatus, 30_000);
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [crawlStatus?.status, loadStatus]);

  const isDirty  = url !== saved;
  const isActive      = IN_PROGRESS.has(crawlStatus?.status);
  const crawlAllowed  = crawlStatus?.crawlAllowed !== false; // default true if not present
  const webTrainings  = crawlStatus?.websiteTrainings ?? null;

  async function handleSave() {
    if (!isDirty || saving) return;
    if (url && !isValidUrl(url)) {
      setSaveError('Please enter a valid URL (e.g. https://example.com).');
      return;
    }
    setSaveError('');
    setSaving(true);
    try {
      await saveWebsiteConfig(url);
      setSaved(url);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2500);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleTrain() {
    if (training || isActive) return;
    setTraining(true);
    try {
      await trainWebsite();
      await loadStatus();
    } catch {
      // silently ignore train errors
    } finally {
      setTraining(false);
    }
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#475569', fontSize: 13 }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
        style={{ animation: 'spin 0.8s linear infinite' }}>
        <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
      </svg>
      Loading…
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
        Enter your website URL. Pulse will crawl this site and learn from its content to answer customer questions.
      </p>

      {/* URL input */}
      <input
        type="url"
        value={url}
        onChange={e => { setUrl(e.target.value); setSaveError(''); setShowSaved(false); }}
        onKeyDown={e => { if (e.key === 'Enter') handleSave(); }}
        placeholder="https://example.com"
        style={{
          outline: 'none', width: '100%', boxSizing: 'border-box',
          border: `1.5px solid ${saveError ? 'rgba(239,68,68,0.5)' : 'rgba(71,158,195,0.25)'}`,
          background: 'rgba(15,23,42,0.7)', color: '#e2e8f0',
          borderRadius: 9, padding: '10px 14px',
          fontSize: 13.5, fontFamily: 'inherit',
          transition: 'border-color .15s',
        }}
        onFocus={e => { if (!saveError) e.target.style.borderColor = 'rgba(71,158,195,0.55)'; }}
        onBlur={e => { if (!saveError) e.target.style.borderColor = 'rgba(71,158,195,0.25)'; }}
      />

      {/* Save row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!isDirty || saving}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 24px', borderRadius: 9, border: 'none',
            cursor: !isDirty || saving ? 'not-allowed' : 'pointer',
            background: !isDirty || saving ? 'rgba(30,41,59,0.6)' : 'linear-gradient(135deg,#479EC3,#22D3EE)',
            color: !isDirty || saving ? '#334155' : '#fff',
            fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
            transition: 'background .2s',
          }}
        >
          Save
        </button>
        <span style={{
          fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
          color: saving ? '#64748b' : '#10b981',
          opacity: saving || showSaved ? 1 : 0, transition: 'opacity .2s', pointerEvents: 'none',
        }}>
          {saving ? (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}>
                <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
              </svg>
              Saving…
            </>
          ) : (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Saved
            </>
          )}
        </span>
      </div>

      {saveError && (
        <p style={{ fontSize: 13, color: '#f87171', margin: 0, padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          {saveError}
        </p>
      )}

      <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

      {/* Train section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div className="wt-train-row" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={handleTrain}
            disabled={training || isActive || !saved || isDirty || !crawlAllowed}
            title={
              !crawlAllowed   ? 'Monthly website training limit reached' :
              isDirty         ? 'Save your URL first' :
              !saved          ? 'Enter a URL first' : undefined
            }
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 24px', borderRadius: 9, border: 'none',
              cursor: training || isActive || !saved || isDirty || !crawlAllowed ? 'not-allowed' : 'pointer',
              background: training || isActive || !saved || isDirty || !crawlAllowed ? 'rgba(30,41,59,0.6)' : 'linear-gradient(135deg,#479EC3,#22D3EE)',
              color: training || isActive || !saved || isDirty || !crawlAllowed ? '#334155' : '#fff',
              fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
              transition: 'background .2s',
            }}
          >
            {training || isActive ? (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                  style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}>
                  <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
                </svg>
                Training…
              </>
            ) : 'Train'}
          </button>

          {!statusLoading && crawlStatus?.status && (
            <StatusChip
              status={crawlStatus.status}
              crawlCompletedAt={crawlStatus.crawlCompletedAt}
              websiteUrl={crawlStatus.websiteUrl}
            />
          )}

          {webTrainings?.total != null && (
            <div className="wt-usage" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#64748b' }}>
                {webTrainings.used}/{webTrainings.total} trainings used
              </span>
              <div style={{ width: 80, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 3,
                  width: `${Math.min(100, (webTrainings.used / webTrainings.total) * 100)}%`,
                  background: webTrainings.remaining === 0 ? '#f87171' : webTrainings.used / webTrainings.total >= 0.7 ? '#fbbf24' : '#22D3EE',
                  transition: 'width 0.4s',
                }} />
              </div>
            </div>
          )}
        </div>

        <p style={{ fontSize: 12.5, color: '#64748b', margin: 0, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          Please make sure the URL is correct for your website.
        </p>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @media (max-width: 540px) {
          .wt-usage { margin-left: 0 !important; }
        }
      `}</style>
    </div>
  );
}
